import { ArrowDownToLine } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useReportExport from '../hooks/useReportExport';
import HiddenReportExportHost from '../shared/HiddenReportExportHost';
import { ViewType, type State } from '../shared/useListReducer';
import TrainersNotesEditor from './TrainersNotesEditor';
import OverallTraineeReportContent from './components/OverallTraineeReportContent';
import useOverallTraineeReportData from './hooks/useOverallTraineeReportData';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Loader from '@/components/common/Loader';
import PageHeader from '@/components/common/PageHeader';

interface OverallTraineeReportPageProps {
    handleBack: () => void;
    state: State;
}

const OverallTraineeReportPage = ({ handleBack, state }: OverallTraineeReportPageProps) => {
    const { t } = useTranslation('overallTraineeReport');
    const hasAutoExported = useRef(false);

    const {
        assessmentRunIdSelected: assessmentRunId = '',
        userIdSelected: userId = '',
        programIdSelected: programId,
        programNameSelected: programName,
        startDateSelected: startDate,
        endDateSelected: endDate,
        editMode,
        viewType,
    } = state;

    const reportData = useOverallTraineeReportData({
        assessmentRunId,
        userId,
        programId,
        programName,
        startDate,
        endDate,
    });

    const { exportRef, downloading, exportPdf } = useReportExport();

    useEffect(() => {
        hasAutoExported.current = false;
    }, [assessmentRunId, userId, viewType]);

    const handleDownload = useCallback(async () => {
        if (downloading || !exportRef.current) {
            return;
        }

        try {
            await exportPdf('IndividualReport.pdf');
            if (viewType === ViewType.DownloadReport) {
                handleBack?.();
            }
        } catch (error) {
            console.error('Download failed:', error);
        }
    }, [downloading, exportRef, exportPdf, handleBack, viewType]);

    useEffect(() => {
        if (
            reportData.loading ||
            reportData.error ||
            !reportData.hasAnyAssessmentItems ||
            viewType === ViewType.ShowReport ||
            hasAutoExported.current
        ) {
            return;
        }

        hasAutoExported.current = true;

        const timeoutId = setTimeout(() => {
            handleDownload();
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [reportData.loading, reportData.error, reportData.hasAnyAssessmentItems, viewType, handleDownload]);

    const headerActionButton = {
        label: downloading ? t('export.loading') : t('export.button'),
        onClick: handleDownload,
        icon: <ArrowDownToLine className="size-5" />,
        disabled: downloading,
    };

    if (reportData.error) {
        return (
            <EmptyContainer>
                <Empty title={t('empty.title')} />
            </EmptyContainer>
        );
    }

    if (reportData.loading && !reportData.hasAnyAssessmentItems) {
        return <Loader />;
    }

    return (
        <>
            {viewType === ViewType.ShowReport && (
                <div className="relative w-full min-w-[1000px] overflow-auto">
                    <PageHeader
                        title={t('header.back')}
                        actionBack={{ showBackButton: true, onBack: handleBack }}
                        actionButton={editMode ? undefined : headerActionButton}
                        className="w-full min-w-[600px]"
                    />

                    {editMode && (
                        <div className="mb-6 px-0.5">
                            <TrainersNotesEditor
                                assessmentRunId={assessmentRunId}
                                userId={userId}
                                onBack={handleBack}
                            />
                        </div>
                    )}

                    <OverallTraineeReportContent data={reportData} editMode={editMode} />
                </div>
            )}
            <HiddenReportExportHost exportRef={exportRef}>
                <OverallTraineeReportContent data={reportData} optimizeForPdf editMode={editMode} />
            </HiddenReportExportHost>
        </>
    );
};

export default OverallTraineeReportPage;
